import SafeHtml from '../../components/SafeHtml';
import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import HomeDashboardLayout from '../HomeDashboardLayout';
import HeaderProfileSection from '../HeaderProfileSection';
import Searchbar from './Searchbar';
import { fetchSearchCategories } from '../../services/searchService';
import { DASHBOARD_ROUTES } from '../dashboardRoutes';
import { SEARCH_ROUTES } from './searchRoutes';
import { useAuth } from '../../context/AuthContext';
import { addRecentSearch, apiColor, readSearchSession, writeSearchSession, getSearchRouteState, navigateSearch } from './searchFlow';
import SearchAdvert from './SearchAdvert';
import '../../dashboard/Dashboard.css';
import '../HomeDashboard.css';
import '../DashboardSkeleton.css';
import './SearchCategories.css';
import '../questionBank/QuestionBankList.css';

const categoryAppearance = {
  0: { icon: 'bi-ui-checks-grid', iconClass: 'quiz', route: SEARCH_ROUTES.testSeries },
  1: { icon: 'bi-ui-checks-grid', iconClass: 'quiz', route: SEARCH_ROUTES.quizPyp },
  2: { icon: 'bi-journal-bookmark-fill', iconClass: 'question', route: SEARCH_ROUTES.questions },
  3: { icon: 'bi-play-btn', iconClass: 'question', route: SEARCH_ROUTES.lectures },
  4: { icon: 'bi-book-half', iconClass: 'notes', route: SEARCH_ROUTES.shortNotes },
  7: { icon: 'bi-mortarboard', iconClass: 'question', route: SEARCH_ROUTES.exams },
  8: { icon: 'bi-book-half', iconClass: 'news', route: SEARCH_ROUTES.blogs },
};

function SearchRowsSkeleton() {
  return <div className="question-bankk" role="status" aria-busy="true" aria-label="Loading search categories">
    {Array.from({ length: 6 }, (_, index) => <div className="question-bank__itemk" key={index} aria-hidden="true">
      <div className="question-bank__leftk">
        <span className="question-bank__iconk dashboard-skeleton search-category-skeleton-icon" />
        <div className="question-bank__contentk">
          <span className="dashboard-skeleton search-category-skeleton-title" />
          <span className="dashboard-skeleton search-category-skeleton-count" />
        </div>
      </div>
      <span className="dashboard-skeleton search-category-skeleton-arrow" />
    </div>)}
  </div>;
}

const SearchCategories = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const client = useQueryClient();
  const { user } = useAuth();
  const sessionKey = `categories:${user?.id || user?.user_id || 'current'}`;
  const restored = navigationType === 'POP' || location.state?.restoreSearch ? readSearchSession(sessionKey) : null;
  const entryFlow = getSearchRouteState(location.pathname, location.state);
  const entryKeyword = String(entryFlow.entryKeyword || '').trim();
  const [input, setInput] = useState(() => entryKeyword || restored?.input || '');
  const [submittedKeyword, setSubmittedKeyword] = useState(() => entryKeyword || restored?.submittedKeyword || '');
  const [requestVersion, setRequestVersion] = useState(() => entryKeyword ? Number(entryFlow.entrySearchId) || 0 : restored?.requestVersion || 0);
  const [hasSearched, setHasSearched] = useState(() => Boolean(entryKeyword || restored?.hasSearched));
  const [recent, setRecent] = useState(() => entryKeyword && !restored ? [entryKeyword] : restored?.recent || []);

  const categoriesQuery = useQuery({
    queryKey: ['searchCategories', user?.id || user?.user_id, user?.preferred_lang || user?.content_language || 'EN', submittedKeyword, requestVersion],
    queryFn: ({ signal }) => fetchSearchCategories(submittedKeyword, { signal }),
    enabled: Boolean(submittedKeyword),
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
  });

  useEffect(() => {
    writeSearchSession(sessionKey, { input, submittedKeyword, requestVersion, hasSearched, recent });
  }, [sessionKey, input, submittedKeyword, requestVersion, hasSearched, recent]);

  const submitSearch = (value = input) => {
    const keyword = value.trim();
    if (!keyword) return;
    client.cancelQueries({ queryKey: ['searchCategories'] });
    setInput(keyword);
    setSubmittedKeyword(keyword);
    setRequestVersion((version) => version + 1);
    setHasSearched(true);
    setRecent((previous) => addRecentSearch(previous, keyword));
    navigateSearch(navigate, SEARCH_ROUTES.categories, { restoreSearch: true }, { replace: true });
  };

  const clearSearch = () => {
    client.cancelQueries({ queryKey: ['searchCategories'] });
    setInput('');
    setSubmittedKeyword('');
    setHasSearched(false);
    navigateSearch(navigate, SEARCH_ROUTES.categories, { restoreSearch: true }, { replace: true });
  };

  const openCategory = (category) => {
    const appearance = categoryAppearance[Number(category.category_id)];
    if (!appearance || !submittedKeyword) return;
    writeSearchSession(sessionKey, { input, submittedKeyword, requestVersion, hasSearched, recent });
    navigateSearch(navigate, appearance.route, {
        searchData: category,
        searchController: submittedKeyword,
        searchLogId: categoriesQuery.data?.searchLogId || 0,
        fromSearchCategories: true,
    });
  };

  const categories = categoriesQuery.data?.limitReached ? [] : categoriesQuery.data?.categories || [];
  const loading = categoriesQuery.isFetching && !categories.length;
  const empty = hasSearched && submittedKeyword && !loading && !categoriesQuery.isError && !categories.length;

  return <>
    <style>{`.homeDashboard-wrapper .page-content { padding-right: 0; }`}</style>
    <HomeDashboardLayout showHeader={false}>
      <div className="qbl-page qbankList-page searchCategry">
        <div className="qbl-main">
          <section className="qbl-panel">
            <div className="hideProfile-page">
              <div className="categories-page-header mb-0">
                <div>
                  <button type="button" className="categories-back-button" onClick={() => window.history.length > 1 ? navigate(-1) : navigate(DASHBOARD_ROUTES.home)} aria-label="Go back">
                    <i className="bi bi-chevron-left" />
                  </button>
                </div>
                <div />
                <div><HeaderProfileSection /></div>
              </div>
            </div>

            <Searchbar value={input} onChange={(value) => { setInput(value); if (!value.trim()) { client.cancelQueries({ queryKey: ['searchCategories'] }); setSubmittedKeyword(''); } }} onSearch={() => submitSearch()} onClear={clearSearch} isLoading={categoriesQuery.isFetching} />
            <SearchAdvert position="search_top" />

            <div className="qb-body">
              <div className="d-flex align-items-center justify-content-center">
                <div className="qb-copy">
                  <h1 className="qb-title">Search <span>Categories</span></h1>
                  <p className="qb-sub qbb-list">{submittedKeyword || 'Practice more , score higher'}</p>
                </div>
              </div>

              {loading && <SearchRowsSkeleton />}
              {categoriesQuery.isError && <div className="search-category-message" role="alert">
                <p>{categoriesQuery.error?.message || 'Unable to search right now.'}</p>
                <button type="button" onClick={() => categoriesQuery.refetch()}>Retry</button>
              </div>}
              {!loading && !categoriesQuery.isError && categories.length > 0 && <div className="question-bankk">
                {categories.map((category, index) => {
                  const appearance = categoryAppearance[Number(category.category_id)] || { icon: 'bi-search', iconClass: 'question' };
                  return <button type="button" className="question-bank__itemk search-category-action" key={`${category.category_id}-${index}`} onClick={() => openCategory(category)} disabled={!appearance.route}>
                    <div className="question-bank__leftk">
                      <div className={`question-bank__iconk question-bank__iconk--${appearance.iconClass}`} style={{ color: apiColor(category.border_color_code) || apiColor(category.background_color_code) }}>
                        <i className={`bi ${appearance.icon}`} aria-hidden="true" />
                      </div>
                      <div className="question-bank__contentk">
                        <SafeHtml className="question-bank__titlek" value={category.category_name || 'Search category'} />
                        <div className="question-bank__countk">{Number(category.count) || 0} <SafeHtml as="span" inline value={category.message || 'items'} /></div>
                      </div>
                    </div>
                    {appearance.route && <i className="bi bi-arrow-up-right question-bank__arrowk" aria-hidden="true" />}
                  </button>;
                })}
              </div>}
              {empty && <div className="search-category-message">
                <p>{categoriesQuery.data?.limitReached ? categoriesQuery.data.message || 'Search limit reached.' : 'No Results Found'}</p>
              </div>}
              {recent.length > 0 && !loading && <div className="search-category-history">
                <p>Recent Searches</p>
                <div className="question-bankk">
                  {recent.map((term) => <button type="button" className="question-bank__itemk search-category-action" key={term.toLowerCase()} onClick={() => submitSearch(term)}>
                    <div className="question-bank__leftk">
                      <div className="question-bank__iconk question-bank__iconk--question"><i className="bi bi-clock-history" aria-hidden="true" /></div>
                      <div className="question-bank__titlek">{term}</div>
                    </div>
                    <i className="bi bi-arrow-up-right question-bank__arrowk" aria-hidden="true" />
                  </button>)}
                </div>
              </div>}
            </div>
          </section>
        </div>
      </div>
    </HomeDashboardLayout>
  </>;
};

export default SearchCategories;
